import type { ParamParseKey, Params } from 'react-router';
import { generatePath, matchPath, useLocation } from 'react-router';

import { Navigate } from '@sorare/routing';
import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_MARKET,
  ANY_SPORT_MY_CLUB_SLUG,
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_BOARDS_PRO,
  ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
  ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_LINEUP_LINEUP,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
  ANY_SPORT_PLAY_EVENTTYPE_LINEUPS_ID,
  ANY_SPORT_SERIES_MARKET,
  ANY_SPORT_SERIES_PLAY,
  ANY_SPORT_SERIES_SQUAD,
  ANY_SPORT_SERIES_TASK_LINEUPS,
  INVITE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { sendSafeError } from '@sorare/core/src/lib/error';
import type { PathParams } from '@sorare/core/src/lib/routing/generateSportPath';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { FIXTURES } from '@sorare/gameplay/src/lib/so5';

function parseIntent(uri: string) {
  // HACK: Firefox and Chrome currently do not parse non special urls correctly
  // https://github.com/nodejs/node/issues/37192#issuecomment-772004612
  // We work around this by setting a special scheme to extract host, pathname and searchParams
  const { host, pathname, searchParams } = new URL(
    uri.replace(/^sorare:\/\//, 'http://')
  );

  return { host, pathname, searchParams };
}

const matchDeeplink = <InitialPath extends string, TargetPath extends string>(
  uri: string,
  path: InitialPath,
  to: TargetPath,
  args?: (
    params: Params<ParamParseKey<InitialPath>>,
    searchParams: URLSearchParams
  ) => { params: PathParams<TargetPath>; sport: Sport }
) => {
  const { host, pathname, searchParams } = parseIntent(uri);

  const pattern = `/${host}${pathname}`;
  const match = matchPath(path, pattern);
  if (!match) {
    return null;
  }

  if (!args) {
    return generatePath(to);
  }

  return generateSportPath(to, args(match.params, searchParams));
};

const deeplinkSportToSport = (sport: string | undefined): Sport => {
  if (!sport) {
    return Sport.FOOTBALL;
  }
  if (sport === 'so5') {
    return Sport.FOOTBALL;
  }
  if (sport === 'mlb') {
    return Sport.BASEBALL;
  }
  if (sport.toUpperCase() in Sport) {
    return Sport[sport.toUpperCase() as Sport];
  }
  return Sport.FOOTBALL;
};

// The flutter app redirects to this page when it receives a push notification with a deeplink. This hook maps the deeplink to the right page
const useResolveDeeplink = (
  uri: string | null | undefined
): null | string | { to: string; state: unknown } => {
  const { currentUser } = useCurrentUserContext();

  if (!uri || !currentUser) {
    sendSafeError(new Error('Deeplink without uri or currentUser'));
    return null;
  }

  const matches = [
    // GAMEPLAY
    matchDeeplink(
      uri,
      '/:sport/:fixtureType/fixture',
      ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
      ({ sport, fixtureType }, searchParams) => {
        const fixture = {
          completed: FIXTURES.past,
          upcoming: FIXTURES.upcoming,
        }[searchParams.get('type') || 'upcoming'];

        return {
          params: {
            eventType: (fixtureType && fixtureType in So5FixtureEvent
              ? So5FixtureEvent[fixtureType as So5FixtureEvent]
              : So5FixtureEvent.CLASSIC
            ).toLowerCase(),
            fixture: (fixture || 'upcoming').toLowerCase(),
          },
          sport: deeplinkSportToSport(sport),
        };
      }
    ),
    matchDeeplink(
      uri,
      '/:sport/fixture',
      ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
      ({ sport }, searchParams) => {
        const fixture = {
          completed: FIXTURES.past,
          upcoming: FIXTURES.upcoming,
        }[searchParams.get('type') || 'upcoming'];

        return {
          params: {
            eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
            fixture: (fixture || 'upcoming').toLowerCase(),
          },
          sport: deeplinkSportToSport(sport),
        };
      }
    ),
    matchDeeplink(
      uri,
      '/:sport/:fixtureType/composeTeam',
      ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_LINEUP_LINEUP,
      ({ sport, fixtureType }, searchParams) => {
        return {
          params: {
            eventType: (fixtureType && fixtureType in So5FixtureEvent
              ? So5FixtureEvent[fixtureType as So5FixtureEvent]
              : So5FixtureEvent.CLASSIC
            ).toLowerCase(),
            leaderboard: searchParams.get('slug') || '',
            lineup: searchParams.get('lineup_id') || '',
          },
          sport: deeplinkSportToSport(sport),
        };
      }
    ),
    matchDeeplink(
      uri,
      '/:sport/composeTeam',
      ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
      ({ sport }, searchParams) => ({
        params: {
          eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
          leaderboard: searchParams.get('slug') || '',
        },
        sport: deeplinkSportToSport(sport),
      })
    ),
    matchDeeplink(
      uri,
      '/:sport/lineup',
      ANY_SPORT_PLAY_EVENTTYPE_LINEUPS_ID,
      ({ sport }, searchParams) => ({
        params: {
          eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
          id: searchParams.get('id') || '',
        },
        sport: deeplinkSportToSport(sport),
      })
    ),
    matchDeeplink(
      uri,
      '/:sport/play/career',
      ANY_SPORT_PLAY_BOARDS_PRO,
      ({ sport }) => ({
        params: {},
        sport: deeplinkSportToSport(sport),
      })
    ),
    // MARKET
    matchDeeplink(uri, '/:sport/market', ANY_SPORT_MARKET, ({ sport }) => ({
      params: {},
      sport: deeplinkSportToSport(sport),
    })),
    matchDeeplink(
      uri,
      '/so5/market/commonMarket',
      generateSportPath(ANY_SPORT_SERIES_MARKET, {
        sport: Sport.FOOTBALL,
      }),
      () => ({
        params: {},
        sport: Sport.FOOTBALL,
      })
    ),
    // SERIES
    matchDeeplink(
      uri,
      '/:sport/play/series/squad',
      ANY_SPORT_SERIES_SQUAD,
      ({ sport }) => ({
        params: {},
        sport: deeplinkSportToSport(sport),
      })
    ),
    // Must be matched before the /:sport/play/series/:taskId catch-all,
    // which would otherwise match a no-source connect deeplink with taskId="connect"
    matchDeeplink(
      uri,
      '/:sport/play/series/connect/:source?',
      `${ANY_SPORT_SERIES_PLAY}?dialog=connect`,
      ({ sport }) => ({
        params: {},
        sport: deeplinkSportToSport(sport),
      })
    ),
    matchDeeplink(
      uri,
      '/:sport/play/series/:taskId',
      ANY_SPORT_SERIES_TASK_LINEUPS,
      ({ sport, taskId }) => ({
        params: { task: taskId || '' },
        sport: deeplinkSportToSport(sport),
      })
    ),
    matchDeeplink(
      uri,
      '/:sport/play/series',
      ANY_SPORT_SERIES_PLAY,
      ({ sport }) => ({
        params: {},
        sport: deeplinkSportToSport(sport),
      })
    ),
    // GLOBAL
    matchDeeplink(uri, '/:sport/home', ANY_SPORT_PLAY, ({ sport }) => ({
      params: {},
      sport: deeplinkSportToSport(sport),
    })),
    matchDeeplink(
      uri,
      '/managerprofile',
      ANY_SPORT_MY_CLUB_SLUG,
      ({ sport }, searchParams) => ({
        params: { slug: searchParams.get('slug') || '' },
        sport: deeplinkSportToSport(sport),
      })
    ),
    matchDeeplink(uri, '/referrals', INVITE),
  ];

  const match = matches.find(m => m !== null);
  if (match) {
    return match;
  }

  // Path doesn't exist, fall back to error handling
  sendSafeError(new Error(`Unhandled deeplink with unknown path: ${uri}`));
  return null;
};

export const Deeplink = () => {
  const { search } = useLocation();
  const uri = /uri=(.*)/.exec(search)?.[1];
  const decodedUri = decodeURIComponent(uri || '');
  const to = useResolveDeeplink(decodedUri);

  if (to === null) {
    const { host, pathname } = parseIntent(decodedUri);
    const fallbackPath = `/${host}${pathname}`;
    return <Navigate to={fallbackPath} replace />;
  }

  if (typeof to === 'string') {
    return <Navigate to={to} replace />;
  }

  return <Navigate to={to.to} state={to.state} replace />;
};

export default Deeplink;
